/**
 * 어드민 API 클라이언트.
 * - VITE_ADMIN_API_BASE에서 base URL 결정 (없으면 동일 도메인 fallback).
 * - 401 응답은 자동 세션 폐기.
 */
import { clearAdminSession, getAdminSession } from './auth';

const API_BASE = (import.meta.env.VITE_ADMIN_API_BASE as string | undefined) ?? '';

export interface AdminLoginRequest {
  email: string;
  password: string;
}

export interface AdminLoginResponse {
  token: string;
  email: string;
  expires_at: string;
}

export interface ApiError {
  status: number;
  error: string;
  detail?: unknown;
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.auth !== false) {
    const session = getAdminSession();
    if (session) headers['Authorization'] = `Bearer ${session.token}`;
  }
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) {
    if (res.status === 401) clearAdminSession();
    const body = await res.json().catch(() => ({}));
    const err: ApiError = { status: res.status, error: body?.error ?? `http-${res.status}`, detail: body };
    throw err;
  }
  return (await res.json()) as T;
}

// =====================================================================================
// Users
// =====================================================================================
export interface AdminUserRow {
  id: string;
  phone_e164: string;
  name: string | null;
  email: string | null;
  company_name: string | null;
  created_at: string;
  onboarded_at: string | null;
  deleted_at: string | null;
  this_month_cost: number;
  this_month_events: number;
}

export interface AdminUsersResponse {
  items: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminUserDetail {
  user: {
    id: string;
    phone_e164: string;
    name: string | null;
    email: string | null;
    company_name: string | null;
    company_role: string | null;
    created_at: string;
    onboarded_at: string | null;
    deleted_at: string | null;
  };
  activity: { calls: number; chat_messages: number; todos: number; summaries: number };
  this_month_usage: Array<{
    source: string;
    feature: string;
    events: number;
    cost: number;
    input_units: number;
    output_units: number;
  }>;
}

// =====================================================================================
// Cost
// =====================================================================================
export interface AdminCostSummary {
  today_cost: number;
  yesterday_cost: number;
  this_month_cost: number;
  last_month_cost: number;
  today_events: number;
  this_month_events: number;
}

export interface AdminCostBySourcePoint {
  day: string; // YYYY-MM-DD KST
  source: string;
  cost: number;
}

export interface AdminCostByFeatureRow {
  source: string;
  feature: string;
  cost: number;
  events: number;
}

export interface AdminTopUserRow {
  user_id: string;
  name: string | null;
  email: string | null;
  phone_e164: string;
  cost: number;
  events: number;
}

// =====================================================================================
// Applications (이용 신청)
// =====================================================================================
export type ApplicationStatus = 'new' | 'contacted' | 'invited' | 'declined';

export interface AdminApplicationRow {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  companyName: string | null;
  companyRole: string | null;
  message: string | null;
  source: string | null;
  status: ApplicationStatus;
  contactedAt: string | null;
  operatorNotes: string | null;
  createdAt: string;
}

export interface AdminApplicationsResponse {
  items: AdminApplicationRow[];
  total: number;
  limit: number;
  offset: number;
}

export const adminApi = {
  login: (body: AdminLoginRequest) =>
    request<AdminLoginResponse>('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify(body),
      auth: false,
    }),
  me: () => request<{ email: string }>('/api/admin/me'),
  logout: () => request<{ ok: true }>('/api/admin/logout', { method: 'POST' }),

  listUsers: (opts: { q?: string; limit?: number; offset?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return request<AdminUsersResponse>(`/api/admin/users${qs ? `?${qs}` : ''}`);
  },
  getUser: (id: string) => request<AdminUserDetail>(`/api/admin/users/${id}`),

  costSummary: () => request<AdminCostSummary>('/api/admin/cost/summary'),
  costBySource: () =>
    request<{ items: AdminCostBySourcePoint[] }>('/api/admin/cost/by-source'),
  costByFeature: () =>
    request<{ items: AdminCostByFeatureRow[] }>('/api/admin/cost/by-feature'),
  costTopUsers: (limit = 10) =>
    request<{ items: AdminTopUserRow[] }>(`/api/admin/cost/top-users?limit=${limit}`),

  listApplications: (opts: { status?: ApplicationStatus; limit?: number; offset?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return request<AdminApplicationsResponse>(`/api/admin/applications${qs ? `?${qs}` : ''}`);
  },
  updateApplication: (id: string, body: { status?: ApplicationStatus; operator_notes?: string }) =>
    request<AdminApplicationRow>(`/api/admin/applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};
